'use client';

import { memo, useState } from 'react';

import { Check, ChevronLeft, ChevronRight, Crown, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';

import type { AnimeArchetype, CharacterConfig, Gender } from './types';
import { useSubscriptionStatus } from './use-subscription-status';

function GenderIcon({
  gender,
  className,
}: {
  gender: string;
  className?: string;
}) {
  if (gender === 'female') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <circle cx="12" cy="9" r="6" />
        <line x1="12" y1="15" x2="12" y2="22" />
        <line x1="9" y1="19" x2="15" y2="19" />
      </svg>
    );
  }
  if (gender === 'male') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <circle cx="10" cy="14" r="6" />
        <line x1="22" y1="2" x2="15" y2="9" />
        <polyline points="16 2 22 2 22 8" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="11" cy="11" r="5" />
      <line x1="11" y1="16" x2="11" y2="22" />
      <line x1="8" y1="19" x2="14" y2="19" />
      <line x1="19" y1="2" x2="13" y2="8" />
      <polyline points="14 2 19 2 19 7" />
      <line x1="19" y1="14" x2="13" y2="8" />
    </svg>
  );
}

interface StepAnimeArchetypeProps {
  config: CharacterConfig;
  updateConfig: (updates: Partial<CharacterConfig>) => void;
  onBack: () => void;
  onNext: () => void;
}

const archetypeOptions: {
  id: AnimeArchetype;
  maleKey?: string;
  premium?: boolean;
}[] = [
  { id: 'schoolgirl' },
  { id: 'catgirl', maleKey: 'catboy' },
  { id: 'warrior' },
  { id: 'idol' },
  { id: 'demon' },
  { id: 'angel' },
  { id: 'custom', premium: true },
];

function getArchetypeKey(
  option: { id: AnimeArchetype; maleKey?: string },
  gender: Gender,
): string {
  if (option.maleKey && gender !== 'female') return option.maleKey;
  return option.id;
}

function getArchetypeImage(id: string, gender: Gender) {
  return `/images/character-creator/2nd-step/anime-archetype/${gender}/${id}/1.avif`;
}

export const StepAnimeArchetype = memo(function StepAnimeArchetype({
  config,
  updateConfig,
  onBack,
  onNext,
}: StepAnimeArchetypeProps) {
  const { t } = useTranslation('common');
  const { isSubscribed } = useSubscriptionStatus();
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const handleSelect = (id: AnimeArchetype, premium?: boolean) => {
    if (premium && !isSubscribed) return;
    updateConfig({ animeArchetype: id });
    if (id !== 'custom') {
      updateConfig({ animeArchetypeCustom: undefined });
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-2 sm:px-4 lg:px-0">
      <div className="mb-3 text-center sm:mb-4">
        <h1 className="mb-1 text-xl font-bold text-white sm:text-2xl md:text-3xl">
          <Trans i18nKey="common:characterCreator.chooseArchetype" />
        </h1>
        <p className="text-xs text-gray-400 sm:text-sm">
          <Trans i18nKey="common:characterCreator.pickAnimeArchetype" />
        </p>
      </div>

      {/* Gender Badge */}
      <div className="mb-3 flex justify-center sm:mb-4">
        <div className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-red-600 to-pink-600 px-3 py-1.5 text-xs font-semibold text-white">
          <GenderIcon gender={config.gender} className="h-3.5 w-3.5" />
          <span>{t(`filters.${config.gender}`)}</span>
        </div>
      </div>

      <div className="mx-auto grid max-w-[340px] grid-cols-4 gap-2 sm:max-w-md sm:gap-3 md:max-w-2xl md:gap-4 lg:max-w-3xl">
        {archetypeOptions.map((option) => {
          const isSelected = config.animeArchetype === option.id;
          const isLocked = option.premium && !isSubscribed;
          const hasImgError = imgErrors[option.id];
          const label = t(
            `characterCreator.options.archetype.${getArchetypeKey(option, config.gender)}`,
          );

          return (
            <button
              key={option.id}
              onClick={() => handleSelect(option.id, option.premium)}
              className={`relative aspect-[3/4] overflow-hidden rounded-xl transition-all duration-300 ${
                isSelected
                  ? 'border-2 border-pink-500 shadow-[0_0_14px_rgba(236,72,153,0.5)]'
                  : isLocked
                    ? 'border border-white/10 opacity-70'
                    : 'border border-white/10 hover:border-white/30'
              }`}
            >
              {option.id !== 'custom' && !hasImgError ? (
                <img
                  src={getArchetypeImage(option.id, config.gender)}
                  alt={label}
                  className="h-full w-full object-cover"
                  onError={() =>
                    setImgErrors((prev) => ({ ...prev, [option.id]: true }))
                  }
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                  {option.premium ? (
                    <Crown className="h-8 w-8 text-amber-400 sm:h-10 sm:w-10" />
                  ) : (
                    <span className="text-xs font-medium text-gray-400 sm:text-sm">
                      {label}
                    </span>
                  )}
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
              <div className="absolute right-0 bottom-0 left-0 p-1.5 sm:p-2">
                <h3 className="text-center text-xs font-bold text-white sm:text-sm">
                  {label}
                </h3>
              </div>
              {isSelected && !isLocked && (
                <div className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-pink-500 shadow-lg sm:h-5 sm:w-5">
                  <Check className="h-2.5 w-2.5 text-white sm:h-3 sm:w-3" />
                </div>
              )}
              {isLocked && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
                  <Lock className="mb-1.5 h-5 w-5 text-amber-400 sm:h-6 sm:w-6" />
                  <span className="text-xs font-semibold text-amber-300">
                    <Trans i18nKey="common:characterCreator.premiumLabel" />
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {config.animeArchetype === 'custom' && isSubscribed && (
        <div className="mx-auto mt-3 max-w-md sm:mt-4">
          <input
            type="text"
            value={config.animeArchetypeCustom || ''}
            onChange={(e) =>
              updateConfig({ animeArchetypeCustom: e.target.value })
            }
            placeholder={t('characterCreator.archetypeCustomPlaceholder')}
            className="w-full rounded-xl border border-white/20 bg-black/50 px-3 py-2.5 text-sm text-white placeholder-gray-500 transition-all focus:border-pink-500/60 focus:ring-1 focus:ring-pink-500/30 focus:outline-none sm:px-4 sm:py-3"
          />
        </div>
      )}

      {/* Age Slider */}
      <div className="mt-4 mb-3 sm:mt-5 sm:mb-4">
        <p className="mb-2.5 text-center text-xs font-semibold tracking-wider text-gray-500 uppercase">
          <Trans i18nKey="common:characterCreator.ageLabel" />
        </p>
        <div className="mx-auto max-w-xs sm:max-w-sm md:max-w-md">
          <div className="mb-2 flex items-center justify-between sm:mb-3">
            <span className="text-3xl font-bold text-white sm:text-4xl">
              {config.age}
            </span>
            <div className="flex items-center gap-1.5 rounded-full bg-red-600/90 px-3 py-1.5">
              <Check className="h-3 w-3 text-white" />
              <span className="text-xs font-bold text-white">
                <Trans i18nKey="common:characterCreator.verified18plus" />
              </span>
            </div>
          </div>
          <input
            type="range"
            min="18"
            max="55"
            value={config.age}
            onChange={(e) => updateConfig({ age: parseInt(e.target.value) })}
            className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gradient-to-r from-red-600 to-pink-600 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-pink-500 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-lg"
          />
          <div className="mt-1.5 flex justify-between text-xs text-gray-500">
            <span>18</span>
            <span className="font-semibold text-pink-400">
              {t('characterCreator.yearsOldWithCount', { age: config.age })}
            </span>
            <span>
              <Trans i18nKey="common:characterCreator.ageSuffix" />
            </span>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-2.5 sm:gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 rounded-xl border-2 border-white/20 bg-transparent px-5 py-2.5 text-xs font-semibold text-white transition-all hover:border-white/40 hover:bg-white/5 sm:px-6 sm:py-3 sm:text-sm"
        >
          <ChevronLeft className="h-4 w-4" />
          <Trans i18nKey="common:characterCreator.backButton" />
        </button>
        <button
          onClick={onNext}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-red-600 via-pink-600 to-pink-700 px-6 py-2.5 text-xs font-bold text-white shadow-lg shadow-pink-500/30 transition-all hover:from-red-700 hover:via-pink-700 hover:to-pink-800 sm:flex-none sm:px-10 sm:py-3 sm:text-sm"
        >
          <Trans i18nKey="common:characterCreator.nextStep" />
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});
